import React, { useEffect, useRef } from 'react';
import { useStreamProcessorWorker } from '../../hooks/useStreamProcessorWorker';
import logger from '../../utils/logger';

/**
 * Dedicated component for polling updates when a comp is not updated
 * This isolates the polling logic to prevent unnecessary re-renders of the main CaseReview component
 * Now extracts completed work from Web Worker and logs detailed progress
 */
const UpdatePollingComponent = ({ 
  isCompUpdated, 
  updatedArray, 
  compStreamObject, 
  savedCallbackRef 
}) => {
  const intervalRef = useRef(null);
  const lastStreamUpdateCountRef = useRef(0);
  const { getDetailedProgress } = useStreamProcessorWorker();

  useEffect(() => {
    if (!isCompUpdated) {
      logger.log('🔄 Starting worker-aware update polling - comp not updated yet');
      
      // Count how many stream updates we have
      const streamUpdateCount = compStreamObject?.current?.updated?.filter(Boolean).length || 0;
      lastStreamUpdateCountRef.current = streamUpdateCount;
      
      intervalRef.current = setInterval(() => {
        // Get detailed progress from worker
        const workerProgress = getDetailedProgress();
        
        // Only trigger if there are new stream updates
        const currentStreamUpdateCount = compStreamObject?.current?.updated?.filter(Boolean).length || 0;
        
        if (currentStreamUpdateCount > lastStreamUpdateCountRef.current) {
          logger.log(`🔄 New stream updates detected (${currentStreamUpdateCount} vs ${lastStreamUpdateCountRef.current}) - triggering update`);
          lastStreamUpdateCountRef.current = currentStreamUpdateCount;
          
          // Use the ref to get the most current callback
          if (savedCallbackRef?.current) {
            savedCallbackRef.current(false);
          }
        } else if (workerProgress.isProcessing) {
          // Log detailed worker progress even if no new stream updates
          logger.log('🔍 Worker is processing - detailed progress:', {
            isProcessing: workerProgress.isProcessing,
            processedChunks: workerProgress.processedChunks?.length || 0,
            workerReady: workerProgress.workerReady,
            lastProcessedChunk: workerProgress.lastProcessedChunk ? {
              parcelId: workerProgress.lastProcessedChunk.parcelId,
              index: workerProgress.lastProcessedChunk.index,
              hasRegularComps: !!workerProgress.lastProcessedChunk.regularComps,
              hasOptimizedComps: !!workerProgress.lastProcessedChunk.optimizedComps
            } : null
          });
          
          // Log detailed chunk information
          if (workerProgress.processedChunks && workerProgress.processedChunks.length > 0) {
            logger.log('📦 Processed chunks details:', workerProgress.processedChunks.map((chunk, index) => ({
              chunkIndex: index,
              parcelId: chunk.parcelId,
              index: chunk.index,
              regularCompsKeys: chunk.regularComps ? Object.keys(chunk.regularComps).slice(0, 10) : [],
              optimizedCompsKeys: chunk.optimizedComps ? Object.keys(chunk.optimizedComps).slice(0, 10) : []
            })));
          }
          
          // Trigger state update callback when worker is processing (for KNN flow)
          if (savedCallbackRef?.current) {
            logger.log('🔄 Worker is processing - triggering state update callback');
            savedCallbackRef.current(false);
          }
        } else {
          logger.log('🔄 No new stream updates and worker not processing - skipping update');
        }
      }, 100); // More frequent polling to catch worker progress
      
      return () => {
        logger.log('🔄 Clearing worker-aware update polling interval');
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    } else {
      // Clear any existing interval when comp is updated
      if (intervalRef.current) {
        logger.log('🔄 Comp is updated, clearing polling interval');
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [isCompUpdated, compStreamObject, getDetailedProgress]); // Include getDetailedProgress in dependencies

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // This component doesn't render anything - it's purely for side effects
  return null;
};

export default React.memo(UpdatePollingComponent);
